import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { api, ApiError, type MerchantDetail, type NavTechUser, type TaxableEntity } from '../api/client'

export function MerchantDetailPage() {
  const { merchantId = '' } = useParams()
  const { data, isLoading, error } = useQuery({
    queryKey: ['merchant', merchantId],
    queryFn: () => api.merchants.detail(merchantId),
    enabled: Boolean(merchantId),
  })

  if (isLoading) return <div>Loading merchant…</div>
  if (error) return <div className="text-red-600">Failed to load: {(error as Error).message}</div>
  if (!data) return null

  return (
    <div className="max-w-4xl">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <div className="text-xs text-slate-500">Merchant</div>
          <h1 className="text-xl font-bold">{data.displayName}</h1>
          <div className="text-xs font-mono text-slate-500">{data.id}</div>
        </div>
        <Link to="/integrators" className="text-sm text-brand-700 hover:underline">← integrators</Link>
      </div>

      <ConnectionsSection detail={data} />
      <TaxableEntitiesSection detail={data} />
      <NavUsersSection detail={data} />
    </div>
  )
}

function ConnectionsSection({ detail }: { detail: MerchantDetail }) {
  return (
    <section className="mb-6">
      <h2 className="mb-2 text-sm font-semibold uppercase text-slate-600">Integrator connections</h2>
      <div className="overflow-hidden rounded border border-slate-200 bg-white">
        <table className="w-full text-xs">
          <thead className="bg-slate-100 text-left uppercase text-slate-600">
            <tr>
              <th className="px-3 py-2">Integrator</th>
              <th className="px-3 py-2">External ID</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Since</th>
            </tr>
          </thead>
          <tbody>
            {detail.connections.map((c) => (
              <tr key={c.id} className="border-t border-slate-200">
                <td className="px-3 py-2 font-mono">{c.integratorId}</td>
                <td className="px-3 py-2 font-mono">{c.externalId}</td>
                <td className="px-3 py-2">{c.status}</td>
                <td className="px-3 py-2 text-slate-500">{new Date(c.validFrom).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function TaxableEntitiesSection({ detail }: { detail: MerchantDetail }) {
  const qc = useQueryClient()
  const [integratorId, setIntegratorId] = useState(detail.connections[0]?.integratorId ?? '')
  const [countryCode, setCountryCode] = useState('HU')
  const [taxNumber, setTaxNumber] = useState('')
  const [entityType, setEntityType] = useState('NORMAL')
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    if (!integratorId && detail.connections[0]) {
      setIntegratorId(detail.connections[0].integratorId)
    }
  }, [detail.connections, integratorId])

  const add = useMutation({
    mutationFn: () => {
      const resolved = integratorId || detail.connections[0]?.integratorId || ''
      if (!resolved) throw new Error('no integrator connection available')
      return api.merchants.addTaxableEntity(detail.id, {
        integratorId: resolved, countryCode, taxNumber: taxNumber.trim(), entityType,
      })
    },
    onSuccess: () => {
      setTaxNumber('')
      setErr(null)
      qc.invalidateQueries({ queryKey: ['merchant', detail.id] })
    },
    onError: (e) => setErr(e instanceof ApiError ? `${e.status}: ${e.message}` : (e as Error).message),
  })

  return (
    <section className="mb-6">
      <h2 className="mb-2 text-sm font-semibold uppercase text-slate-600">Taxable entities</h2>
      <div className="mb-2 overflow-hidden rounded border border-slate-200 bg-white">
        <table className="w-full text-xs">
          <thead className="bg-slate-100 text-left uppercase text-slate-600">
            <tr>
              <th className="px-3 py-2">Country</th>
              <th className="px-3 py-2">Tax number</th>
              <th className="px-3 py-2">Entity type</th>
              <th className="px-3 py-2">Integrator</th>
              <th className="px-3 py-2">Valid</th>
            </tr>
          </thead>
          <tbody>
            {detail.taxableEntities.map((t: TaxableEntity) => (
              <tr key={t.id} className={`border-t border-slate-200 ${t.validTo ? 'text-slate-400 line-through' : ''}`}>
                <td className="px-3 py-2">{t.countryCode}</td>
                <td className="px-3 py-2 font-mono">{t.taxNumber}</td>
                <td className="px-3 py-2">{t.entityType}</td>
                <td className="px-3 py-2 font-mono">{t.integratorId.slice(0, 8)}…</td>
                <td className="px-3 py-2 text-slate-500">
                  {new Date(t.validFrom).toLocaleDateString()} → {t.validTo ? new Date(t.validTo).toLocaleDateString() : '∞'}
                </td>
              </tr>
            ))}
            {detail.taxableEntities.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-4 text-center text-slate-500">No taxable entities.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <form
        className="flex flex-wrap items-end gap-2 rounded border border-slate-200 bg-white p-3"
        onSubmit={(e) => { e.preventDefault(); if (taxNumber.trim()) add.mutate() }}
      >
        <label className="text-xs text-slate-600">Integrator
          <select value={integratorId} onChange={(e) => setIntegratorId(e.target.value)}
                  className="mt-1 block w-56 rounded border border-slate-300 px-2 py-1 text-sm">
            {detail.connections.map((c) => (
              <option key={c.id} value={c.integratorId}>{c.integratorId.slice(0, 8)}… ({c.externalId})</option>
            ))}
          </select>
        </label>
        <label className="text-xs text-slate-600">Country
          <input value={countryCode} onChange={(e) => setCountryCode(e.target.value.toUpperCase())} maxLength={2}
                 className="mt-1 block w-16 rounded border border-slate-300 px-2 py-1 text-sm" />
        </label>
        <label className="text-xs text-slate-600">Tax number
          <input value={taxNumber} onChange={(e) => setTaxNumber(e.target.value)} placeholder="12345678"
                 className="mt-1 block w-40 rounded border border-slate-300 px-2 py-1 text-sm font-mono" />
        </label>
        <label className="text-xs text-slate-600">Entity type
          <select value={entityType} onChange={(e) => setEntityType(e.target.value)}
                  className="mt-1 block w-32 rounded border border-slate-300 px-2 py-1 text-sm">
            {['NORMAL', 'GROUP_MEMBER', 'GROUP_MEMBER_OWN', 'VAT_GROUP'].map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </label>
        <button type="submit" disabled={add.isPending || !taxNumber.trim()}
                className="rounded bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-40">
          {add.isPending ? 'Adding…' : 'Add entity'}
        </button>
        {err && <span className="text-sm text-red-600">{err}</span>}
      </form>
    </section>
  )
}

function NavUsersSection({ detail }: { detail: MerchantDetail }) {
  const qc = useQueryClient()
  const activeEntities = detail.taxableEntities.filter((t) => !t.validTo)
  const [integratorId, setIntegratorId] = useState(detail.connections[0]?.integratorId ?? '')
  const [taxableEntityId, setTaxableEntityId] = useState(activeEntities[0]?.id ?? '')

  // Sync selects with newly-arrived detail data. Without this, adding a
  // taxable entity leaves the "Register NAV user" select bound to '',
  // and the backend rejects the empty string as "not a UUID".
  useEffect(() => {
    if (!integratorId && detail.connections[0]) {
      setIntegratorId(detail.connections[0].integratorId)
    }
  }, [detail.connections, integratorId])
  useEffect(() => {
    if (!taxableEntityId && activeEntities[0]) {
      setTaxableEntityId(activeEntities[0].id)
    }
  }, [activeEntities, taxableEntityId])
  const [environment, setEnvironment] = useState<'test' | 'production'>('test')
  const [login, setLogin] = useState('')
  const [taxNumber, setTaxNumber] = useState('')
  const [password, setPassword] = useState('')
  const [signKey, setSignKey] = useState('')
  const [exchangeKey, setExchangeKey] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const [ok, setOk] = useState<string | null>(null)

  const register = useMutation({
    mutationFn: () => {
      const resolvedIntegratorId = integratorId || detail.connections[0]?.integratorId || ''
      const resolvedEntityId = taxableEntityId || activeEntities[0]?.id || ''
      if (!resolvedIntegratorId) throw new Error('no integrator connection available')
      if (!resolvedEntityId) throw new Error('no active taxable entity — add one first')
      return api.merchants.registerNavUser(detail.id, {
        integratorId: resolvedIntegratorId,
        taxableEntityId: resolvedEntityId,
        environment,
        login: login.trim(),
        taxNumber: taxNumber.trim(),
        password, signKey, exchangeKey,
      })
    },
    onSuccess: (res) => {
      setErr(null)
      setOk(`Registered — SSM path: ${res.secretRef}`)
      setLogin(''); setTaxNumber(''); setPassword(''); setSignKey(''); setExchangeKey('')
      qc.invalidateQueries({ queryKey: ['merchant', detail.id] })
    },
    onError: (e) => {
      setOk(null)
      setErr(e instanceof ApiError ? `${e.status}: ${e.message}` : (e as Error).message)
    },
  })

  const revoke = useMutation({
    mutationFn: (id: string) => api.merchants.revokeNavUser(detail.id, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['merchant', detail.id] }),
  })

  return (
    <section className="mb-6">
      <h2 className="mb-2 text-sm font-semibold uppercase text-slate-600">NAV technical users</h2>
      <div className="mb-2 overflow-hidden rounded border border-slate-200 bg-white">
        <table className="w-full text-xs">
          <thead className="bg-slate-100 text-left uppercase text-slate-600">
            <tr>
              <th className="px-3 py-2">Env</th>
              <th className="px-3 py-2">Login</th>
              <th className="px-3 py-2">Tax #</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Secret ref</th>
              <th className="px-3 py-2">Last token</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {detail.navTechnicalUsers.map((n: NavTechUser) => (
              <tr key={n.id} className={`border-t border-slate-200 ${n.status !== 'active' ? 'text-slate-400' : ''}`}>
                <td className="px-3 py-2">{n.environment}</td>
                <td className="px-3 py-2 font-mono">{n.login}</td>
                <td className="px-3 py-2 font-mono">{n.taxNumber}</td>
                <td className="px-3 py-2">{n.status}</td>
                <td className="px-3 py-2 font-mono text-[10px] break-all">{n.secretRef}</td>
                <td className="px-3 py-2 text-slate-500">
                  {n.lastTokenExchangeAt ? new Date(n.lastTokenExchangeAt).toLocaleString() : '—'}
                </td>
                <td className="px-3 py-2 text-right">
                  {n.status === 'active' && (
                    <button
                      type="button"
                      disabled={revoke.isPending}
                      onClick={() => { if (confirm('Revoke this NAV user? Existing tokens keep working until they expire.')) revoke.mutate(n.id) }}
                      className="rounded border border-red-300 px-2 py-1 text-red-700 hover:bg-red-50"
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {detail.navTechnicalUsers.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-4 text-center text-slate-500">No NAV technical users.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {activeEntities.length === 0 ? (
        <div className="text-xs text-slate-500">
          Add at least one active taxable entity before registering a NAV technical user.
        </div>
      ) : (
        <form
          className="grid grid-cols-2 gap-3 rounded border border-slate-200 bg-white p-3"
          onSubmit={(e) => {
            e.preventDefault()
            const missing: string[] = []
            if (!login.trim()) missing.push('login')
            if (!taxNumber.trim()) missing.push('taxNumber')
            if (!password) missing.push('password')
            if (!signKey) missing.push('signKey')
            if (!exchangeKey) missing.push('exchangeKey')
            if (missing.length) {
              setOk(null)
              setErr(`missing required fields: ${missing.join(', ')}`)
              return
            }
            register.mutate()
          }}
        >
          <label className="text-xs text-slate-600">Integrator
            <select value={integratorId} onChange={(e) => setIntegratorId(e.target.value)}
                    className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-sm">
              {detail.connections.map((c) => (
                <option key={c.id} value={c.integratorId}>{c.integratorId.slice(0, 8)}… ({c.externalId})</option>
              ))}
            </select>
          </label>
          <label className="text-xs text-slate-600">Taxable entity
            <select value={taxableEntityId} onChange={(e) => setTaxableEntityId(e.target.value)}
                    className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-sm">
              {activeEntities.map((t) => (
                <option key={t.id} value={t.id}>{t.countryCode} · {t.taxNumber} ({t.entityType})</option>
              ))}
            </select>
          </label>
          <label className="text-xs text-slate-600">Environment
            <select value={environment} onChange={(e) => setEnvironment(e.target.value as 'test' | 'production')}
                    className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-sm">
              <option value="test">test</option>
              <option value="production">production</option>
            </select>
          </label>
          <label className="text-xs text-slate-600">Tax number (must match entity)
            <input value={taxNumber} onChange={(e) => setTaxNumber(e.target.value)} placeholder="12345678"
                   className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-sm font-mono" />
          </label>
          <label className="text-xs text-slate-600">Login
            <input value={login} onChange={(e) => setLogin(e.target.value)}
                   className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-sm" />
          </label>
          <label className="text-xs text-slate-600">Password
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                   className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-sm" />
          </label>
          <label className="col-span-2 text-xs text-slate-600">Sign key (XML signature, base64)
            <textarea value={signKey} onChange={(e) => setSignKey(e.target.value)} rows={2}
                      className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-sm font-mono" />
          </label>
          <label className="col-span-2 text-xs text-slate-600">Exchange key (AES-128, base64)
            <textarea value={exchangeKey} onChange={(e) => setExchangeKey(e.target.value)} rows={2}
                      className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-sm font-mono" />
          </label>
          <div className="col-span-2 flex items-center gap-3">
            <button type="submit" disabled={register.isPending}
                    className="rounded bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-40">
              {register.isPending ? 'Registering…' : 'Register NAV user'}
            </button>
            {ok && <span className="text-xs text-emerald-700">{ok}</span>}
            {err && <span className="text-xs text-red-600">{err}</span>}
          </div>
        </form>
      )}
    </section>
  )
}
